/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useEffect, useState } from "react";
import { List, Grid, Typography, Divider } from "@material-ui/core";
import { add, getUnixTime, isValid, startOfDay } from "date-fns";
import { KeyboardDatePicker, KeyboardTimePicker } from "@material-ui/pickers";
import { getOverwritesAsync, updateOverwritesAsync } from "../../services/OverwritesService";
import { OverwritesCard } from "./OverwritesCard";
import { v4 as uuid } from "uuid";
import { useCallback } from "react";
import { EmptyPlaceholder } from "../../../../components";
import { ActionButtons, ProgressOverlay } from "../../../../components/Common";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";

/**
 * Method for creating a new overwrite object.
 * @param {Date} startDate The date and time to start the overwrite.
 * @param {Date} endDate The date and time to end te overwrite.
 * @param {String} queueId The ID of the queue for the overwrite.
 * @param {String} overwriteId The ID for the overwrite.
 */
const createOverwrite = (startDate, endDate, queueId, overwriteId) => {
	const newOverwrite = {
		startDate: startDate.toLocaleString("en-US"),
		endDate: endDate.toLocaleString("en-US"),
		expireTime: getUnixTime(endDate) + 86400,
		overwriteId,
		queueId,
	};
	return newOverwrite;
};

/**
 * This page is used to display and edit the overwrites for a queue.
 * @param {String} queueId The ID of the queue for the overwrites.
 * @param {Function} showSnackbar function to show / hide snackbar
 * @param {Function} setIsLoading function to show / hide loading indicator.
 * @param {Function} setTabModified function to show prompt on tab change.
 */
const HoursOfOperationsOverwritesTab = ({ queue, showSnackbar, setTabModified, setIsLoading }) => {
	const [overwritesToDisplay, setNewOverwritesToDisplay] = useState(undefined);
	const [startDate, setStartDate] = useState(add(new Date(), { days: 1 }));
	const [endDate, setEndDate] = useState(add(new Date(), { days: 1, hours: 1 }));
	const [modifiedState, setModifiedState] = useState({});
	const [confirmPrompt, setConfirmPrompt] = useState(undefined);
	const [editMode, setEditMode] = useState(false);

	useEffect(() => {
		setTabModified(!!Object.keys(modifiedState).find((overwrite) => modifiedState[overwrite]) || editMode);
	}, [modifiedState, setTabModified, editMode]);

	const setOverwritesToDisplay = useCallback((newOverwritesToDisplay) => {
		//sort by time if dates are the same.
		setNewOverwritesToDisplay(newOverwritesToDisplay.sort((a, b) => (a.startDate > b.startDate ? 1 : -1)));
	}, []);

	useEffect(() => {
		const getOverwrites = async () => {
			setIsLoading(true);
			const newOverwrites = await getOverwritesAsync(queue.Id);
			setOverwritesToDisplay([...newOverwrites]);
			setIsLoading(false);
		};
		getOverwrites();
	}, [queue, setOverwritesToDisplay, setIsLoading]);

	const handleItemEditState = useCallback((state, itemId) => {
		setModifiedState((cur) => ({ ...cur, [itemId]: state }));
	}, []);

	const errorMessage = startDate >= endDate ? "End date and time must come after start date and time" : undefined;
	const formatString = "MM/dd/yyyy";
	const addDisabled =
		(overwritesToDisplay &&
			overwritesToDisplay.filter(
				(overwrite) => overwrite.startDate === startDate.toString() && overwrite.endDate === endDate.toString(),
			).length !== 0) ||
		startDate >= endDate ||
		startDate < startOfDay(new Date());
	return (
		<div style={{ width: "100%" }}>
			{confirmPrompt !== undefined && confirmPrompt}
			{overwritesToDisplay && (
				<div>
					<div className="col-12">
						<Grid container justifyContent="space-between" spacing={2} className="mt-3">
							<Grid item className="w-100 d-flex align-items-center justify-content-end flex-wrap">
								<ActionButtons
									className="float-right"
									showCancel
									showSave={editMode}
									onCancel={() => {
										setStartDate(add(new Date(), { days: 1 }));
										setEndDate(add(new Date(), { days: 1, hours: 1 }));
										setEditMode(!editMode);
									}}
									onSave={async () => {
										setIsLoading(true);
										const newOverwrite = createOverwrite(startDate, endDate, queue.Id, uuid());
										await updateOverwritesAsync({ updateOverwrite: newOverwrite }, queue.Id);
										showSnackbar("Successfully created overwrite.", "success");
										setOverwritesToDisplay([...overwritesToDisplay, newOverwrite]);
										setStartDate(add(new Date(), { days: 1 }));
										setEndDate(add(new Date(), { days: 1 }));
										setIsLoading(false);
										setEditMode(false);
									}}
									cancelLabel={editMode ? "Cancel" : "Create Overwrite"}
									disableSave={addDisabled}
									saveLabel="Add Overwrite"
								/>
							</Grid>
							{editMode && (
								<>
									<Grid item xs={12} md={6} className="mt-2 d-flex justify-content-between">
										<KeyboardDatePicker
											disableToolbar
											variant="inline"
											format={formatString}
											margin="normal"
											id="start-date-picker-inline"
											label="Start Date"
											value={startDate}
											minDate={new Date()}
											onChange={(date) => {
												let newStartDate = date;
												if (isValid(newStartDate)) {
													newStartDate.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);
													setStartDate(newStartDate);
												}
											}}
											KeyboardButtonProps={{
												"aria-label": "change date",
											}}
											className="m-0 w-100"
										/>
									</Grid>
									<Grid item xs={12} md={6} className="mt-2">
										<KeyboardTimePicker
											disableToolbar
											className="m-0 w-100"
											label="Start Time"
											variant="inline"
											ampm
											value={startDate}
											minDate={new Date()}
											onChange={(date) => {
												let newStartTime = date;
												if (isValid(newStartTime)) {
													let newStartDate = startDate;
													newStartDate.setHours(newStartTime.getHours(), newStartTime.getMinutes(), 0, 0);
													setStartDate(new Date(newStartDate));
												}
											}}
										/>
									</Grid>
									<Grid item xs={12} md={6} className="mt-2">
										<KeyboardDatePicker
											disableToolbar
											variant="inline"
											format={formatString}
											margin="normal"
											id="end-date-picker-inline"
											label="End Date"
											value={endDate}
											error={startDate > endDate}
											minDate={startDate}
											minDateMessage={""}
											onChange={(newEndTime) => {
												let newEndDate = newEndTime;
												if (isValid(newEndDate)) {
													newEndDate.setHours(endDate.getHours(), endDate.getMinutes(), 0, 0);
													setEndDate(newEndDate);
												}
											}}
											KeyboardButtonProps={{
												"aria-label": "change date",
											}}
											className="m-0 w-100"
										/>
									</Grid>
									<Grid item xs={12} md={6} className="mt-2">
										<KeyboardTimePicker
											disableToolbar
											className="m-0 w-100"
											label="End Time"
											variant="inline"
											ampm
											value={endDate}
											error={startDate > endDate}
											onChange={(date) => {
												let newEndTime = date;
												if (isValid(newEndTime)) {
													let newEndDate = endDate;
													newEndDate.setHours(newEndTime.getHours(), newEndTime.getMinutes(), 0, 0);
													setEndDate(new Date(newEndDate));
												}
											}}
										/>
									</Grid>
									{errorMessage && (
										<Grid item xs={12}>
											<Typography className="text-danger">{errorMessage}</Typography>
										</Grid>
									)}
								</>
							)}
						</Grid>
					</div>
					<List>
						{overwritesToDisplay.length > 0 ? (
							overwritesToDisplay.map((overwrite, index) => {
								const deleteOverwrite = async () => {
									setConfirmPrompt(
										<ConfirmDialog
											message={`Are you sure you want to delete this overwrite?`}
											title={`Delete Overwrite`}
											confirmOpen
											onClose={(confirm) => {
												if (confirm) {
													const update = async () => {
														setIsLoading(true);
														await updateOverwritesAsync({ deleteOverwrite: overwrite }, queue.Id);
														handleItemEditState(false, overwrite.overwriteId);
														const newOverwritesToDisplay = overwritesToDisplay;
														newOverwritesToDisplay.splice(index, 1);
														setOverwritesToDisplay([...newOverwritesToDisplay]);
														setIsLoading(false);
														showSnackbar("Successfully removed overwrite.", "success");
													};
													update();
												}
												setConfirmPrompt(undefined);
											}}
										/>,
									);
								};
								return (
									<React.Fragment key={overwrite.overwriteId}>
										<Divider />
										<OverwritesCard
											key={overwrite.overwriteId}
											deleteOverwrite={deleteOverwrite}
											setItemModified={handleItemEditState}
											setIsLoading={setIsLoading}
											editOverwrite={async (startDate, endDate) => {
												let newOverwritesToDisplay = overwritesToDisplay;
												const newOverwrite = createOverwrite(startDate, endDate, queue.Id, overwrite.overwriteId);
												await updateOverwritesAsync({ updateOverwrite: newOverwrite }, queue.Id);
												newOverwritesToDisplay[index] = newOverwrite;
												setOverwritesToDisplay(newOverwritesToDisplay);
												showSnackbar("Successfully saved overwrite.", "success");
											}}
											overwrite={overwrite}
										/>
									</React.Fragment>
								);
							})
						) : (
							<EmptyPlaceholder message="There are currently no overwrites configured for the queue." />
						)}
					</List>
				</div>
			)}
			{!overwritesToDisplay && <ProgressOverlay />}
		</div>
	);
};

export default HoursOfOperationsOverwritesTab;
